import { LoadedInfo, LoadInIframeResult } from '.';
import { IframeLoadOptions } from './loadComponentInIframe';
import { CreateFromCodeProps, RehydrateFromSnapshotProps } from './createComponentInDiv';
import { loadComponentInIframeInternal } from './Iframe/Outside/loadComponentInIframeInternal';
import { NamespaceLoggerAdapter, getEnvironmentConfigFromRing } from '@fluidx/utilities';
import { InitializeConfig } from './Iframe-shared/InitializeConfig';
import { npmVersionNumber } from './npm.package.version';

export interface CreateFromCodeInIframeProps extends CreateFromCodeProps {
  componentDiv: HTMLElement;
}

export interface RehydrateFromSnapshotInIframeProps extends RehydrateFromSnapshotProps {
  componentDiv: HTMLElement;
}

export interface CreateInIframeResult extends LoadInIframeResult {
  loadedInfo: Promise<Omit<LoadedInfo, 'realize'>>;
}

/**
 * [Experimental]
 * Creates a detached container and loads its default component the same way loadComponentInIframe does
 */
export function createComponentFromCodeInIframe(
  props: CreateFromCodeInIframeProps,
  options: IframeLoadOptions
): CreateInIframeResult {
  return createOrRehydrateHelper(
    props,
    options,
    {
      type: 'CreateFromCode',
      driverType: props.driverType,
      codeDetails: props.codeDetails,
      heroComponentConfiguration: props.heroComponentConfiguration,
      fromExistingTemplateUrl: props.fromExistingTemplateUrl
    },
    Date.now() /* bootstrapperStartTime */
  );
}

/**
 * [Experimental]
 * Rehydrates a detached container from snapshot
 */
export function rehydrateComponentFromSnapshotInIframe(
  props: RehydrateFromSnapshotInIframeProps,
  options: IframeLoadOptions
): CreateInIframeResult {
  return createOrRehydrateHelper(
    props,
    options,
    { type: 'RehydrateFromSnapshot', snapshot: props.snapshot, driverType: props.driverType },
    Date.now() /* bootstrapperStartTime */
  );
}

function createOrRehydrateHelper(
  props: CreateFromCodeInIframeProps | RehydrateFromSnapshotInIframeProps,
  options: IframeLoadOptions,
  initializeConfig: InitializeConfig,
  bootstrapperStartTime: number
) {
  const { codeLoader, logger: hostLogger, environment, ...partialConfig } = props.config || {};
  const logger =
    hostLogger &&
    new NamespaceLoggerAdapter('OfficeWebHost', hostLogger, {
      inIframe: true,
      owhVersion: npmVersionNumber
    });

  // If `HostConfig.environment` is missing, fall back to the iframe environment as a reasonable default.
  const newConfig = {
    ...partialConfig,
    environment: environment || getEnvironmentConfigFromRing(options.iframeEnvironment)
  };

  return loadComponentInIframeInternal(
    initializeConfig,
    props.scope.TokenProvider,
    props.scope,
    props.componentDiv,
    newConfig,
    logger,
    bootstrapperStartTime,
    options
  );
}
